import React, { useState, useEffect, useMemo } from "react";
import {
  useSearchParams,
  useOutletContext,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import * as orderStatus from "../constants/status";
import {
  Button,
  Form,
  Alert,
  ConfigProvider,
  Spin,
  Result,
  DatePicker,
  Select,
  Input,
  Drawer,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import locale from "antd/locale/ru_RU";
import "dayjs/locale/ru";
import axios from "axios";
import dayjs from "dayjs";
import { shippingTypes } from "../search/constants";
import { transportBodyTypes, transportCategories } from "../search/categories";
import {
  loadTypes as loadTypesDict,
  transformToSelect,
} from "../../constants/transport";

import Price from "./Price";
import Info from "./Info";
import Contacts from "./Contacts";
import GoogleMapsInput from "./GoogleMapsInput";
import AiAutofill from "./AiAutofill";
import FloatingField from "../common/FloatingField";

import validations from "../../utils/validations";
import { useSelector } from "react-redux";
import closeIcon from "../../assets/images/close-icon.svg";

const { RangePicker } = DatePicker;

const OrderForm = (props) => {
  // Try to get context from outlet, but handle case where it's not available
  const outletContext = useOutletContext();
  // Allow direct props to override outlet context
  const setShow = props.setShow || (outletContext && outletContext.setShow);
  // We'll use navigate instead of setSelected
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [display, setDisplay] = useState(null);

  useEffect(() => {
    if (localStorage.getItem("display-grid")) {
      setDisplay(localStorage.getItem("display-grid"));
    } else {
      const body = document.querySelector("body");
      if (body.classList.contains("small-grid")) {
        setDisplay("small-grid");
      } else {
        setDisplay("big-grid");
      }
    }

    return () => {
      const body = document.querySelector("body");
      body.classList.remove("big-grid");
      body.classList.remove("small-grid");
    };
  }, []);

  useEffect(() => {
    if (display) {
      const body = document.querySelector("body");
      if (display === "big-grid") {
        body.classList.add("big-grid");
        body.classList.remove("small-grid");
        localStorage.setItem("display-grid", "big-grid");
      } else {
        body.classList.add("small-grid");
        body.classList.remove("big-grid");
        localStorage.setItem("display-grid", "small-grid");
      }
    }
  }, [display]);

  const [isLargeDesktop, setIsLargeDesktop] = useState(window.innerWidth > 1100);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
      setIsLargeDesktop(window.innerWidth > 1100);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('mobile-other-level-view', isMobile);

    return () => {
      document.body.classList.remove('mobile-other-level-view');
    };
  }, [isMobile]);

  useEffect(() => {
    setDrawerOpen(true);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleCloseDrawers = () => setDrawerOpen(false);
    window.addEventListener('closeMobileDrawers', handleCloseDrawers);
    return () => window.removeEventListener('closeMobileDrawers', handleCloseDrawers);
  }, []);

  useEffect(() => {
    const handleOpenOrderDrawer = () => setDrawerOpen(true);
    window.addEventListener('openOrderDrawer', handleOpenOrderDrawer);
    return () => window.removeEventListener('openOrderDrawer', handleOpenOrderDrawer);
  }, []);
  const user = useSelector((state) => state.auth.user);
  const fullName =
    user.first_name && user.last_name
      ? user.first_name + " " + user.last_name
      : null;

  const [searchParams] = useSearchParams();
  const { id: orderIdFromParams } = useParams();
  const [order, setOrder] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [errors, setError] = useState(null);
  const [loading, setLoading] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [created, setCreated] = useState(false);
  const [payloadStatus, setPayloadStatus] = useState(null);
  const [latLng, setLatLng] = useState({
    from: {},
    to: {},
  });
  const [, forceUpdate] = useState(0);
  const loadTypes = transformToSelect(loadTypesDict);
  const [loadTypesState, setLoadTypes] = useState(loadTypes);
  const [transportationType, setTransportationType] = useState(null);

  const [form] = Form.useForm();
  const watchedValues = Form.useWatch([], form) || {};

  // Simple function to get load type validation rules
  const getLoadTypeRules = () => {
    const typeOfTransportation = form.getFieldValue("type_of_transportation");
    const transportCategory = form.getFieldValue("transport_category");

    // Not required if type_of_transportation == 3 and transport_category == 4
    if (typeOfTransportation === 3 || transportCategory === 4) {
      return [];
    }

    return [validations.required];
  };

  useEffect(() => {
    // Check if there is an order ID from URL params
    if (orderIdFromParams) {
      // Check current path to determine if we're duplicating
      const isDuplicating =
        window.location.pathname.includes("/orders/duplicate/");
      // Fetch the order details using the order ID from URL params
      fetchOrder(orderIdFromParams, isDuplicating);
    } else {
      // Check if there is an order query parameter (backwards compatibility)
      const orderParam = searchParams.get("order");
      // Check for duplicate parameter (backwards compatibility)
      const duplicateParam = searchParams.get("duplicate");

      if (duplicateParam) {
        // Fetch the order details for duplication
        fetchOrder(duplicateParam, true);
      } else if (orderParam) {
        // Fetch the order details using the order ID from query params
        fetchOrder(orderParam);
      } else {
        console.log(form);
        // form.resetFields();
        setTimeout(() => {
          clearForm();
        }, 0);
      }
    }
  }, [searchParams, orderIdFromParams]);

  const clearForm = () => {
    // Create an object with all fields set to empty/default values
    const emptyFormValues = {
      distance: null,
      route_polyline: null,
      from_country_code: null,
      from_country: null,
      to_country_code: null,
      to_country: null,
      direction: null,
      from_address: "",
      to_address: "",
      dates: null,
      radiusFrom: null,
      radiusTo: null,
      shippingType: null,
      transport_category: null,
      body_type: null,
      weight: null,
      volume: null,
      length: null,
      width: null,
      height: null,
      price: null,
      load_type: [],
      contact_name:
        user?.first_name && user?.last_name
          ? `${user.first_name} ${user.last_name}`
          : "",
      phone_number: user?.phone_number || "",
      email: user?.email || "",
      company_name: user?.profile?.company_profile?.company_name || "",
      additional_info: "",
      // Add any other fields that need clearing
    };

    // Set all fields to empty/default values
    form.setFieldsValue(emptyFormValues);

    // Reset related state
    setLatLng({
      from: {},
      to: {},
    });
    setSelectedCategory(null);
    setLoadTypes(loadTypes); // Reset to original load types
    // Reset order and duplicate state to ensure date picker is empty on create
    setOrder(null);
    setDuplicate(null);
  };

  const fetchOrder = async (orderId, isDuplicating = false) => {
    try {
      setLoading(true);
      const { data, status } = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/${orderId}/`,
        {
          headers: {
            Authorization: `JWT ${window.localStorage.getItem("access")}`,
          },
        },
      );

      if (status === 200) {
        // Format the data for the form
        const formattedData = formatModel(data);

        if (formattedData.type_of_transportation) {
          setTransportationType(+formattedData.type_of_transportation);
        }

        // If duplicating, clear any identifier fields so a new order is created
        if (isDuplicating) {
          setDuplicate(data);
          // Don't set the order state since we're creating a new order
          // Clear any unique identifiers to ensure a new order is created
          delete formattedData.uuid;
          formattedData.status = orderStatus.ORDER_DRAFT; // Set as draft by default
        } else {
          // Only set the order state if we're editing an existing order
          setOrder(data);
        }

        // Update form with the fetched data
        form.setFieldsValue(formattedData);

        if (data.from_date && data.to_date) {
          form.setFieldsValue({
            dates: [dayjs(data.from_date), dayjs(data.to_date)],
          });
        }

        // Update lat/lng if available
        if (data.location_from) {
          setLatLng((prev) => ({
            ...prev,
            from: {
              lat: data.location_from.lat,
              lng: data.location_from.lng,
            },
          }));
        }

        if (data.location_to) {
          setLatLng((prev) => ({
            ...prev,
            to: {
              lat: data.location_to.lat,
              lng: data.location_to.lng,
            },
          }));
        }
      }
    } catch (error) {
      setError("Failed to load order data");
    } finally {
      setLoading(false);
    }
  };

  // Generate body type options with groups
  const bodyTypeOptions = useMemo(() => {
    // Find all parent body types (with parent_id: null)
    const parentBodyTypes = transportBodyTypes.filter(
      (item) => item.parent_id === null,
    );

    // Filter parent body types based on selected category if any
    const filteredParentTypes = selectedCategory
      ? parentBodyTypes.filter((parent) =>
        parent.category.includes(selectedCategory),
      )
      : parentBodyTypes;

    return filteredParentTypes.map((parent) => (
      <Select.OptGroup key={parent.id} label={parent.name}>
        {transportBodyTypes
          .filter((child) => child.parent_id === parent.id)
          .map((child) => (
            <Select.Option key={child.id} value={child.id}>
              {child.name}
            </Select.Option>
          ))}
      </Select.OptGroup>
    ));
  }, [selectedCategory]);

  const changeLoadTypes = (value = []) => {
    const selectedValues = Array.isArray(value) ? value : [];

    if (selectedValues.includes("4") || selectedValues.includes(4)) {
      // 4 - С полной растентовкой
      if (selectedValues.length > 1 && +selectedValues[0] === 4) {
        // remove first element from array
        const newVal = selectedValues.slice(1);
        setLoadTypes(newVal);
        form.setFieldsValue({ load_type: newVal });
      } else {
        const newLoadTypes = loadTypesState.map((loadType) => {
          if (+loadType.value !== 4) {
            return {
              ...loadType,
              disabled: true,
            };
          }
          return loadType;
        });
        setLoadTypes(newLoadTypes);
        form.setFieldsValue({ load_type: ["4"] });
      }

      forceUpdate((n) => n + 1);
    } else if (selectedValues.length > 0) {
      const newLoadTypes = loadTypesState.map((loadType) => {
        if (+loadType.value === 4) {
          return {
            ...loadType,
            disabled: true,
          };
        }
        return loadType;
      });
      setLoadTypes(newLoadTypes);
      const filteredLoadTypes = selectedValues.filter((loadType) => +loadType !== 4);
      form.setFieldsValue({ load_type: filteredLoadTypes });
      forceUpdate((n) => n + 1);
    } else {
      setLoadTypes(loadTypes);
    }
  };

  const getDistance = async (from, to) => {
    return new Promise(async (resolve, reject) => {
      // Check if we have valid coordinates
      if (!from?.lat || !from?.lng || !to?.lat || !to?.lng) {
        console.error("Invalid coordinates for distance calculation");
        return reject("Invalid coordinates");
      }

      try {
        const [fromLocationData, toLocationData] = await Promise.all([
          checkLocationLevel(from.lat, from.lng),
          checkLocationLevel(to.lat, to.lng),
        ]);

        // If either location is country-level, don't calculate distance
        if (!fromLocationData.isCity || !toLocationData.isCity) {
          form.setFieldsValue({ route_polyline: null });
          form.setFieldsValue({ distance: null });
          return resolve({ distance: null, polyline: null, fromLocationData, toLocationData });
        }
        // Both are cities, proceed with distance calculation
        const directionsService = new window.google.maps.DirectionsService();

        const origin = new window.google.maps.LatLng(from.lat, from.lng);
        const destination = new window.google.maps.LatLng(to.lat, to.lng);

        const request = {
          origin,
          destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        };

        directionsService.route(request, function (result, status) {
          if (status === window.google.maps.DirectionsStatus.OK) {
            const distanceText = result.routes[0].legs[0].distance.text;
            const polyline = result.routes[0].overview_polyline;

            // Also update the form field
            form.setFieldsValue({ route_polyline: polyline });
            form.setFieldsValue({ distance: distanceText });

            // Return the distance text for the payload
            resolve({ distance: distanceText, polyline, fromLocationData, toLocationData });
          } else {
            console.error("Directions request failed due to", status);
            reject(status);
          }
        });
      } catch (error) {
        console.error("Error checking location level:", error);
        // If API check fails, still try to calculate distance
        const directionsService = new window.google.maps.DirectionsService();

        const origin = new window.google.maps.LatLng(from.lat, from.lng);
        const destination = new window.google.maps.LatLng(to.lat, to.lng);

        const request = {
          origin,
          destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        };

        directionsService.route(request, function (result, status) {
          if (status === window.google.maps.DirectionsStatus.OK) {
            const distanceText = result.routes[0].legs[0].distance.text;
            const polyline = result.routes[0].overview_polyline;

            form.setFieldsValue({ route_polyline: polyline });
            form.setFieldsValue({ distance: distanceText });

            resolve({ distance: distanceText, polyline });
          } else {
            console.error("Directions request failed due to", status);
            reject(status);
          }
        });
      }
    });
  };

  // Helper function to check location level using Google Geocoding API
  const checkLocationLevel = (lat, lng) => {
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      const latLng = new window.google.maps.LatLng(lat, lng);

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          const result = results[0];

          // Check location specificity using geocoder types
          const types = result.types || [];

          // Country-level locations typically have only 'country' and 'political' types
          const isCountryLevel =
            types.includes("country") &&
            !types.includes("locality") &&
            !types.includes("administrative_area_level_2") &&
            !types.includes("sublocality");

          let country = null;
          let country_code = null;
          if (result.address_components) {
            const countryComponent = result.address_components.find(c => c.types.includes("country"));
            if (countryComponent) {
              country = countryComponent.long_name;
              country_code = countryComponent.short_name;
            }
          }

          resolve({
            isCity: !isCountryLevel,
            types: types,
            formatted_address: result.formatted_address,
            country,
            country_code,
          });
        } else {
          console.error("Geocoder failed or no results");
          // If geocoding fails, assume it's a city to allow distance calculation
          resolve({ isCity: true, types: [], formatted_address: "", country: null, country_code: null });
        }
      });
    });
  };

  const onSubmit = async (values) => {
    const payload = toPayload(values);

    payload.location_from = latLng.from;
    payload.location_to = latLng.to;

    // Reset error state
    setError(null);

    try {
      if (latLng?.from?.lat && latLng?.to?.lat) {
        try {
          const dist = await getDistance(latLng.from, latLng.to);
          if (dist.distance) {
            payload.distance = dist.distance;
          } else {
            payload.distance = null;
          }
          if (dist.polyline) {
            payload.route_polyline = dist.polyline;
          } else {
            payload.route_polyline = null;
          }
          if (dist.fromLocationData?.country) payload.from_country = dist.fromLocationData.country;
          if (dist.fromLocationData?.country_code) payload.from_country_code = dist.fromLocationData.country_code;
          if (dist.toLocationData?.country) payload.to_country = dist.toLocationData.country;
          if (dist.toLocationData?.country_code) payload.to_country_code = dist.toLocationData.country_code;

        } catch (distanceError) {
          console.error("Failed to calculate distance:", distanceError);
          // Continue with submission without distance
          payload.distance = null;
          payload.route_polyline = null;
        }
      }

      // Check if we're on the duplicate route
      const isDuplicating =
        window.location.pathname.includes("/orders/duplicate/");
      // console.log("payload", payload);
      // return;

      // If duplicating, always use POST to create a new order regardless of having an order object
      const url = isDuplicating
        ? "orders/"
        : order
          ? `orders/${order.uuid}/`
          : "orders/";
      await axios({
        url: `${process.env.REACT_APP_API_URL}/${url}`,
        data: payload,
        // If duplicating, use POST method regardless of having an order object
        method: isDuplicating ? "POST" : order ? "PATCH" : "POST",
        headers: {
          Authorization: `JWT ${window.localStorage.getItem("access")}`,
        },
      });
      setPayloadStatus(payload.status);
      setCreated(true);
      setLoading(false);
      form.resetFields();
    } catch (error) {
      setError("Произошла ошибка!");
      const el = document.querySelector(".middle-sidebar__wrapper");
      el.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      Object.keys(error.response.data).map((key) => {
        if (key === "item" || key === "contacts") {
          Object.keys(error.response.data[key]).map((keyInner) => {
            form.setFields([
              {
                name: keyInner,
                errors: [error.response.data[key][keyInner][0]],
              },
            ]);
          });
        } else {
          form.setFields([
            {
              name: key,
              errors: [error.response.data[key][0]],
            },
          ]);
        }
      });
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    console.log("failed");
  };

  const onPublish = () => {
    form
      .validateFields()
      .then((values) => {
        // Form is valid, submit with draft status
        onSubmit({
          ...values,
          status: 1,
        });
      })
      .catch((errorInfo) => {
        setError("Форма содержит ошибки. Исправьте ошибки перед сохранением.");
        const el = document.querySelector(".middle-sidebar__wrapper");
        el.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
  };

  const saveDraft = () => {
    // Only save as draft if form is valid
    form
      .validateFields()
      .then((values) => {
        // Form is valid, submit with draft status
        onSubmit({
          ...values,
          status: 5,
        });
      })
      .catch((errorInfo) => {
        // If validation fails, show error and don't save

        setError("Форма содержит ошибки. Исправьте ошибки перед сохранением.");
        const el = document.querySelector(".middle-sidebar__wrapper");
        el.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
  };

  const onClose = () => {
    if (payloadStatus === 5) {
      navigate("/my-orders?selected=draft");
    } else {
      navigate("/my-orders");
    }
  };

  console.log("display", display);

  const isSmallGridLargeDesktop = display === "small-grid" && isLargeDesktop;

  const fromNode = (
    <div
      className="transport-search-form__row transport-search-form__row--child-full order-create__location order-create__location--from"
      style={{ flexDirection: "column" }}
    >
      <div
        className="order-form__section__title"
        style={{ paddingLeft: 0 }}
      >
        <span className="order-form__section__title__text">
          Загрузка
        </span>
      </div>
      <GoogleMapsInput
        form={form}
        required={true}
        label="Откуда забрать груз"
        setLatLng={setLatLng}
        direction="from"
        field="from_address"
        latLng={latLng}
        placeholder="*Откуда забрать, город, рег., обл."
        floatingLabel
      />
    </div>
  );

  const toNode = (
    <div
      className="transport-search-form__row transport-search-form__row--child-full order-create__location order-create__location--to"
      style={{ flexDirection: "column" }}
    >
      <div
        className="order-form__section__title"
        style={{ paddingLeft: 0, marginTop: isMobile ? 0 : undefined }}
      >
        <span className="order-form__section__title__text">
          Выгрузка
        </span>
      </div>
      <GoogleMapsInput
        label="Куда доставить груз"
        form={form}
        setLatLng={setLatLng}
        direction="to"
        field="to_address"
        latLng={latLng}
        placeholder="*Куда доставить груз"
        floatingLabel
      />
    </div>
  );

  const datesNode = (
    <div
      className="transport-search-form__row transport-search-form__row--child-full"
      style={isSmallGridLargeDesktop ? { flexDirection: "column" } : {}}
    >
      {isSmallGridLargeDesktop && (
        <div
          className="order-form__section__title"
          style={{ paddingLeft: 0, marginTop: isMobile ? 0 : undefined }}
        >
          <span className="order-form__section__title__text">
            &nbsp;
          </span>
        </div>
      )}
      <ConfigProvider locale={locale}>
        <Form.Item
          name="dates"
          label=" "
          rules={[validations.required]}
        >
          {order ? (
            <FloatingField label="Дата погрузки" active={watchedValues.dates}>
              <RangePicker
                placeholder={["", ""]}
                size="large"
                locale={locale}
                inputReadOnly={isMobile}
                placement={isMobile ? "bottomLeft" : undefined}
                popupClassName={
                  isMobile ? "mobile-range-picker-dropdown" : undefined
                }
                defaultValue={[
                  dayjs(order.from_date),
                  dayjs(order.to_date),
                ]}
              />
            </FloatingField>
          ) : duplicate ? (
            <FloatingField label="Дата погрузки" active={watchedValues.dates}>
              <RangePicker
                placeholder={["", ""]}
                size="large"
                locale={locale}
                inputReadOnly={isMobile}
                placement={isMobile ? "bottomLeft" : undefined}
                popupClassName={
                  isMobile ? "mobile-range-picker-dropdown" : undefined
                }
                defaultValue={[
                  dayjs(duplicate?.from_date),
                  dayjs(duplicate?.to_date),
                ]}
              />
            </FloatingField>
          ) : (
            <FloatingField label="Дата погрузки" active={watchedValues.dates}>
              <RangePicker
                placeholder={["", ""]}
                size="large"
                locale={locale}
                inputReadOnly={isMobile}
                placement={isMobile ? "bottomLeft" : undefined}
                popupClassName={
                  isMobile ? "mobile-range-picker-dropdown" : undefined
                }
              />
            </FloatingField>
          )}
        </Form.Item>
      </ConfigProvider>
    </div>
  );

  const loadTypeNode = (
    <div className="transport-search-form__row transport-search-form__row--child-full">
      <Form.Item
        rules={getLoadTypeRules()}
        name="load_type"
        label=" "
      >
        <FloatingField label="Тип загрузки (способ)" active={watchedValues.load_type}>
          <Select
            placeholder=""
            options={loadTypes}
            mode="multiple"
            onChange={changeLoadTypes}
            size="large"
          />
        </FloatingField>
      </Form.Item>
    </div>
  );

  const shippingTypeNode = (
    <div
      className="transport-search-form__row transport-search-form__row--child-full"
      style={
        !isSmallGridLargeDesktop && (transportationType === 2 || transportationType === null)
          ? { width: "100%", minWidth: "100%" }
          : {}
      }
    >
      <Form.Item name="type_of_transportation" label=" ">
        <FloatingField label="Тип перевозки" active={watchedValues.type_of_transportation}>
          <Select
            placeholder=""
            options={shippingTypes}
            size="large"
            onChange={(value) => {
              setTransportationType(value);
              // If type is 2, reset count_of_transport field
              if (value === 2) {
                form.setFieldsValue({ count_of_transport: null });
              }
              // Trigger validation update for load_type
              form.validateFields(["load_type"]);
            }}
          />
        </FloatingField>
      </Form.Item>
    </div>
  );

  const countNode = (
    <div className="transport-search-form__row transport-search-form__row--child-full">
      <Form.Item name="count_of_transport" label=" ">
        <FloatingField label="Кол-во машин" active={watchedValues.count_of_transport}>
          <Input
            prefix=""
            type="number"
            size="large"
            className="suffix-input height-44"
          />
        </FloatingField>
      </Form.Item>
    </div>
  );

  const content = (
    <>
      <div className="middle-sidebar__title-wrapper no-bg-title form-page-title">
        <h3 className="middle-sidebar__title mobile-centered-title">
          {order ? "Редактировать груз" : "Отправить новый груз"}
        </h3>
        {!isMobile && (
          <div className="form-page-title__actions">
            <div className="sorting-switcher">
              <div
                className={`sorting-switcher__item ${display === "big-grid" ? "active" : ""}`}
                onClick={() => setDisplay("big-grid")}
              />
              <div
                className={`sorting-switcher__item ${display === "small-grid" ? "active" : ""}`}
                onClick={() => setDisplay("small-grid")}
              />
            </div>
          </div>
        )}
        {isMobile && (
          <img
            src={closeIcon}
            alt="Close"
            onClick={() => navigate(-1)}
            className="form-page-title__close"
          />
        )}
      </div>
      <div className="order-form search__wrapper">
        {!order && !created && !isMobile && (
          <AiAutofill
            form={form}
            setLatLng={setLatLng}
            setSelectedCategory={setSelectedCategory}
            clearForm={clearForm}
          />
        )}
        {created && (
          <Result
            status="success"
            title={order ? "Груз обновлен!" : "Груз создан!"}
            extra={[
              <Button type="default" onClick={onClose}>
                Закрыть
              </Button>,
            ]}
          />
        )}
        {!created && (
          <>
            <Form
              form={form}
              name="basic"
              style={{
                maxWidth: "100%",
                ...(display === "small-grid" ? { gridTemplateColumns: "repeat(1, 1fr)" } : {})
              }}
              onFinish={onSubmit}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              layout="vertical"
              initialValues={
                order
                  ? order
                  : {
                    contact_name: fullName || "",
                    phone_number: user?.phone_number || "",
                    email: user?.email || "",
                    company_name:
                      user?.profile?.company_profile?.company_name || "",
                  }
              }
            >
            <Form.Item hidden name="from_country" />
            <Form.Item hidden name="from_country_code" />
            <Form.Item hidden name="to_country" />
            <Form.Item hidden name="to_country_code" />
            <Form.Item hidden name="distance" />
            <Form.Item hidden name="route_polyline" />
            {errors && (
              <div>
                <Alert message={[errors]} type="error" showIcon />
                <br />
              </div>
            )}
            {loading && (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{
                      fontSize: 32,
                    }}
                    spin
                  />
                }
              />
            )}
            {/* <div className='order-form__section__title'>
            <span className='order-form__section__title__text'>Маршрут</span>
          </div> */}
            {isSmallGridLargeDesktop ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "20px",
                  marginTop: "-25px",
                }}
              >
                {fromNode}
                {toNode}
                {datesNode}
              </div>
            ) : (
              <div
                className="transport-search-form__row transport-search-form__row--stack-mobile"
                style={{ marginTop: "-25px" }}
              >
                {fromNode}
                {toNode}
              </div>
            )}
            {!isSmallGridLargeDesktop && (
              <div className="transport-search-form__row transport-search-form__row--stack-mobile">
                {datesNode}
                {loadTypeNode}
              </div>
            )}
            <div className="order-form__section__title">
              <span className="order-form__section__title__text">
                Требования к транспортировке
              </span>
            </div>
            <div
              className={isSmallGridLargeDesktop ? "" : "transport-search-form__row"}
              style={
                isSmallGridLargeDesktop
                  ? {
                    display: "grid",
                    gridTemplateColumns:
                      transportationType === 1 || transportationType === 3
                        ? "repeat(3, 1fr)"
                        : "repeat(2, 1fr)",
                    gap: "20px",
                  }
                  : {}
              }
            >
              {isSmallGridLargeDesktop && loadTypeNode}
              {shippingTypeNode}
              {(transportationType === 1 || transportationType === 3) && countNode}
            </div>
            <div className="transport-search-form__row transport-search-form__row--stack-mobile">
              <div className="transport-search-form__row transport-search-form__row--child-full">
                <Form.Item name="transport_category" label=" ">
                  <FloatingField label="Категория авто" active={watchedValues.transport_category}>
                    <Select
                      placeholder=""
                      size="large"
                      className={
                        selectedCategory
                          ? `selected-category ${transportCategories.find((cat) => cat.value === selectedCategory)?.className || ""}`
                          : ""
                      }
                      onChange={(value) => {
                        // If category changed, reset the body type field
                        if (value !== selectedCategory) {
                          form.setFieldsValue({ transport_body_type: undefined });
                        }
                        setSelectedCategory(value);
                        // Trigger validation update for load_type
                        form.validateFields(["load_type"]);
                      }}
                    >
                      {transportCategories.map((category) => (
                        <Select.Option
                          key={category.value}
                          value={category.value}
                          className={`transport_category ${category.className}`}
                        >
                          {category.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </FloatingField>
                </Form.Item>
              </div>
              <div className="transport-search-form__row transport-search-form__row--child-full">
                <Form.Item
                  name="transport_body_type"
                  label=" "
                  rules={[validations.required]}
                >
                  <FloatingField label="Тип кузова" active={watchedValues.transport_body_type}>
                    <Select
                      placeholder=""
                      size="large"
                    >
                      {bodyTypeOptions}
                    </Select>
                  </FloatingField>
                </Form.Item>
              </div>
            </div>
            <Info
              form={form}
              order={order}
              duplicate={duplicate}
              watchedValues={watchedValues}
            />
            <Price
              form={form}
              order={order}
              duplicate={duplicate}
              watchedValues={watchedValues}
            />
            <Contacts
              isSmallGridLargeDesktop={isSmallGridLargeDesktop}
              watchedValues={watchedValues}
            />
            <div style={{ display: "flex", gap: "20px" }}>
              <Button
                type="primary"
                htmlType="button"
                onClick={onPublish}
                style={{ maxWidth: "200px", width: "100%" }}
              >
                {order
                  ? order.status === 5
                    ? "Опубликовать"
                    : "Сохранить"
                  : "Опубликовать"}
              </Button>
              <Button
                type="default"
                htmlType="button"
                onClick={saveDraft}
                style={{ maxWidth: "200px", width: "100%", borderColor: "#2F80ED" }}
              >
                Сохранить в черновик
              </Button>
            </div>
            </Form>
            {isMobile && (
              <AiAutofill
                form={form}
                setLatLng={setLatLng}
                setSelectedCategory={setSelectedCategory}
                clearForm={clearForm}
                shouldClearBeforeAutofill={!order}
              />
            )}
          </>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="bottom"
        closable={false}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        height="auto"
        push={false}
        className="mobile-only"
        rootClassName="mobile-drawer-menu-container mobile-form-drawer mobile-drawer-other-level"
      >
        <div style={{ maxHeight: '100%', overflowY: 'auto' }}>
          {content}
        </div>
      </Drawer>
    );
  }

  return content;
};

const toPayload = (payload) => {
  let t = [];
  if (payload.load_type) {
    t = payload.load_type.map((item) => +item);
  }
  return {
    ...payload,
    from_address: payload.from_address,
    to_address: payload.to_address,
    from_date: Array.isArray(payload.dates) ? payload.dates[0].format("YYYY-MM-DD") : payload.dates?.from,
    to_date: Array.isArray(payload.dates) ? payload.dates[1].format("YYYY-MM-DD") : payload.dates?.to,
    payment_form: payload.payment_form,
    payment_method: payload.payment_method,
    nds: payload.nds,
    load_type: t,
    cargo_price: payload.cargo_price,
    item: {
      type: payload.type,
      weight: payload.weight,
      volume: payload.volume,
      length: payload.length,
      width: payload.width,
      height: payload.height,
      count: payload.count,
      additional_requirements: payload.additional_requirements,
      type_of_transport: payload.type_of_transport,
      count_of_transport: payload.count_of_transport,
      additional_load: payload.additional_load,
    },
    price: payload.price || null,
    currency: payload.currency || null,
    contacts: {
      company_name: payload.company_name,
      contact_name: payload.contact_name,
      phone_number: payload.phone_number,
    },
    comment: payload.comment,
  };
};

const formatModel = (_order) => {
  const load_type = _order.load_type.map((item) => item.toString());

  const order = {
    ..._order,
    ..._order.contacts,
    ..._order.item,
    currency: _order.currency,
    load_type,
    type: _order.item.type.toString(),
    additional_load: _order.item.additional_load
      ? _order.item.additional_load.toString()
      : null,
    uuid: _order.uuid,
  };
  delete order.contacts;
  delete order.item;
  return order;
};

export default OrderForm;
